"use client"

import { useEffect, useRef } from "react"
import { useFormContext } from "react-hook-form"
import { animateChildren } from "@/lib/animation-utils"
import { Calendar, Weight, MapPin } from "lucide-react"

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"

export default function WeaningInformationStep() {
  const { control, formState: { dirtyFields } } = useFormContext()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        ;(child as HTMLElement).style.opacity = "0"
        ;(child as HTMLElement).style.transform = "translateY(20px)"
      })

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          )
        }
      })
    }
  }, [])

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">Weaning Information</h2>
        <p className="text-slate-500">Enter details about the calf's weaning process</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={control}
          name="weaningDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Calendar className="h-4 w-4" />
                Weaning Date
                {dirtyFields.weaningDate && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              {dirtyFields.weaningDate && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="weaningWeight"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Weight className="h-4 w-4" />
                Weaning Weight (kg)
                {dirtyFields.weaningWeight && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="Enter weaning weight" {...field} />
              </FormControl>
              {dirtyFields.weaningWeight && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="weaningLocation"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <MapPin className="h-4 w-4" />
                Weaning Location/Farm
                {dirtyFields.weaningLocation && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter location or farm name" {...field} />
              </FormControl>
              {dirtyFields.weaningLocation && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
